// Helpers for the "tournament is about to start" notification in
// LobbyMainContent. Extracted as pure functions so the eligibility logic can
// be exercised by unit tests without standing up the whole lobby tree.
//
// Mirrors Unity's LobbiesModel.LoadLobbies trigger: fire when the player is
// registered (R or P), the tournament is still in the Registering window,
// minimum players have joined, and the start time is within the next 5
// minutes.
//
// SNGs have no scheduled startAtTime — they fire when registered >= minPlayers.

export const ABOUT_TO_START_WINDOW_SECONDS = 5 * 60;

export function isAboutToStart(tournament: any, nowSec: number): boolean {
  if (!tournament) return false;
  if (tournament.status !== 'R') return false;
  const startAt = Number(tournament.startAtTime || 0);
  if (startAt <= 0) return false;
  const delta = startAt - nowSec;
  if (delta <= 0 || delta > ABOUT_TO_START_WINDOW_SECONDS) return false;
  const registered = Number(tournament.registeredPlayers || 0);
  const minPlayers = Number(tournament.minPlayers || 0);
  // Unity gates on `RegisteredPlayers >= MinPlayers`. When the payload omits
  // minPlayers (0 / undefined), treat the gate as satisfied so the player is
  // still warned about a tournament that's about to start.
  if (minPlayers > 0 && registered < minPlayers) return false;
  return true;
}

export const SIT_AND_GO_GAME_TYPE = 'SitAndGo';

// SNGs have no scheduled start time — they begin as soon as enough players
// register. Notify when the minimum player threshold has been reached.
export function isSngAboutToStart(tournament: any): boolean {
  if (!tournament) return false;
  if (tournament.status !== 'R') return false;
  if (tournament.gameType !== SIT_AND_GO_GAME_TYPE) return false;
  // If a scheduled start time exists, the tournament is handled by isAboutToStart instead.
  if (Number(tournament.startAtTime || 0) > 0) return false;
  const registered = Number(
    tournament.registeredPlayers ?? tournament.registered ?? tournament.playerCount ?? 0
  );
  const minPlayers = Number(tournament.minPlayers ?? 0);
  // Require an explicit minPlayers > 0; without it we can't tell if the SNG
  // is actually ready to launch.
  if (minPlayers <= 0) return false;
  return registered >= minPlayers;
}

export function findRegisteredTournaments(
  playerTournamentDetails: any[] | null | undefined,
  tournamentList: any[] | null | undefined,
  sitAndGoList: any[] | null | undefined
): any[] {
  const registeredIds = new Set(
    (playerTournamentDetails || [])
      .filter((d: any) => d?.status === 'R' || d?.status === 'P')
      .map((d: any) => Number(d.tournamentId))
  );
  if (registeredIds.size === 0) return [];
  const all = [...(tournamentList || []), ...(sitAndGoList || [])];
  return all.filter((t: any) => registeredIds.has(Number(t.tournamentId || t.id)));
}

/**
 * Resolve the direct "Join Now" navigation target for a tournament that's
 * about to start — the same rule TournamentDetailModal's "Join Table" button
 * uses so this modal lands the player on their felt without a detail-modal
 * hop:
 *   SNG → the tournament IS the table (use its own gameID)
 *   MTT → the player's assigned seat, read from playerTournamentDetails
 *
 * Returns null when no valid seat is available yet (MTT with gameID `-1`
 * / missing, SNG with no table id) — the caller falls back to opening the
 * tournament detail modal so "Finding the best seat for you…" can guide the
 * player once the engine assigns one.
 *
 * Pure so callers can invoke it from a render-time useCallback without paying
 * a per-render cost (nothing here is a hook or a subscription).
 */
export function resolveAboutToStartJoinTarget(
  tournament: any,
  playerTournamentDetails: any[] | null | undefined
): { gameID: string; game: string } | null {
  if (!tournament) return null;
  const gameType = String(tournament.game || 'texas');
  const isSng = tournament.gameType === SIT_AND_GO_GAME_TYPE;
  const pickTableId = (raw: any): string | null => {
    if (raw === undefined || raw === null || raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null; // -1 / 0 mean "not assigned yet"
    return String(raw);
  };
  if (isSng) {
    const gid = pickTableId(tournament.gameID);
    return gid ? { gameID: gid, game: gameType } : null;
  }
  const tid = Number(tournament.tournamentId ?? tournament.id);
  if (!Number.isFinite(tid)) return null;
  const detail = (playerTournamentDetails || []).find(
    (d: any) =>
      Number(d?.tournamentId) === tid && (d?.status === 'R' || d?.status === 'P')
  );
  if (!detail) return null;
  const gid = pickTableId(detail.gameID);
  return gid ? { gameID: gid, game: gameType } : null;
}

export function pickAboutToStartCandidate(
  registeredTournaments: any[],
  nowSec: number,
  firedIds: Set<string>
): any | null {
  const candidates = registeredTournaments
    .filter((t: any) => {
      const id = String(t.tournamentId || t.id || '');
      if (!id) return false;
      if (firedIds.has(id)) return false;
      return isAboutToStart(t, nowSec) || isSngAboutToStart(t);
    })
    .sort((a: any, b: any) => {
      // Scheduled tournaments sort by startAtTime (soonest first); SNGs
      // (no startAtTime) are appended after all scheduled candidates.
      const aTime = Number(a.startAtTime || 0);
      const bTime = Number(b.startAtTime || 0);
      if (aTime > 0 && bTime > 0) return aTime - bTime;
      if (aTime > 0) return -1;
      if (bTime > 0) return 1;
      return 0;
    });
  return candidates[0] || null;
}
